import { isFeatureEnabled } from "@/feature";
import { createHandlerContext } from "@/stores/context";

import { NatsRpcBackend } from "./NatsRpcBackend";
import type { HandlerContext, RpcBackend } from "./RpcBackend";
import { SwitchingRpcBackend } from "./SwitchingRpcBackend";
import { WampRpcBackend } from "./WampRpcBackend";
import { registerAdoAdapters } from "./adapters/adoAdapters";
import { registerAlmCommonAdapters } from "./adapters/almCommonAdapters";
import { registerAlmConfigAdapters } from "./adapters/almConfigAdapters";
import { registerAlmSyncAdapters } from "./adapters/almSyncAdapters";
import { registerCompanyAdapters } from "./adapters/companyAdapters";
import { registerFlexboardAdapters } from "./adapters/flexboardAdapters";
import { registerJiraConnectionAdapters } from "./adapters/jiraConnectionAdapters";
import { registerOnboardingAdapters } from "./adapters/onboardingAdapters";
import { registerRallyAdapters } from "./adapters/rallyAdapters";
import { registerSessionAdapters } from "./adapters/sessionAdapters";

/**
 * Build a transport-switching RpcBackend from a handler context.
 *
 * Handlers pass their own context (the object they receive at construction);
 * it defaults to `createHandlerContext()` for callers that have none. Explicit
 * injection keeps the backend testable without an active Pinia store.
 *
 * Always returns a SwitchingRpcBackend. It routes per call via the master
 * `nats-piadmin` kill switch AND the per-group `nats-piadmin-<group>` flag
 * (plus per-method capability + the `useBackend` override), resolving the NATS
 * connection live so it self-heals across (re)connects; calls with no live
 * connection or an unregistered method fall to WAMP. The
 * `?feature=nats-piadmin[-<group>]` query override is honoured via `context.route`
 * (populated by createHandlerContext); with no route it falls back to the Unleash
 * toggle only.
 */
export function createRpcBackend(
  context: HandlerContext = createHandlerContext() as HandlerContext,
): RpcBackend {
  const wamp = new WampRpcBackend(context);

  // The NATS backend is built lazily against the *live* connection
  // (`context.natsConnection` is a live getter, not a snapshot). It is (re)built
  // when the connection first lands and again after a reconnect (new connection
  // object), so a handler constructed before the async connect settles still
  // routes NATS once the connection is up — no snapshot-at-construction race.
  // It captures company, user, role and scheme at construction, so a role change
  // on WAMP reconnect or a scheme change on refresh must rebuild it as well, even
  // while the connection object survives: the cache is keyed on every input.
  let nats: NatsRpcBackend | null = null;
  let builtWith: unknown = null;
  let builtFor: string | null = null;
  const getNats = (): NatsRpcBackend | null => {
    const connection = context.natsConnection;
    if (!connection || !context.user?.id) {
      nats = null;
      builtWith = null;
      builtFor = null;
      return null;
    }
    const identity = JSON.stringify([
      context.company,
      context.user.id,
      context.user.role,
      context.natsScheme,
    ]);
    if (nats && builtWith === connection && builtFor === identity) {
      return nats;
    }
    nats = new NatsRpcBackend(
      connection,
      context.company,
      context.user.id,
      context.user.role,
      context.natsScheme,
    );
    registerSessionAdapters(nats);
    registerCompanyAdapters(nats);
    registerJiraConnectionAdapters(nats);
    registerAdoAdapters(nats);
    registerRallyAdapters(nats);
    registerAlmCommonAdapters(nats);
    registerAlmConfigAdapters(nats);
    registerFlexboardAdapters(nats);
    registerOnboardingAdapters(nats);
    registerAlmSyncAdapters(nats);
    builtWith = connection;
    builtFor = identity;
    return nats;
  };

  return new SwitchingRpcBackend(getNats, wamp, (flag) => isFeatureEnabled(context.route, flag));
}
